import { ChangeDetectionStrategy, Component, inject, signal, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AppRoutes, AuthRoutes, ENV_CONFIG, EnvConfig, PLATFORM_TOKEN, PlatformProvider } from '@core';
import { Observable, take } from 'rxjs';
import { AuthWithAppleHandler } from '../../application/handlers/auth-with-apple.handler';
import { AuthWithGoogleHandler } from '../../application/handlers/auth-with-google.handler';
import { LoginHandler } from '../../application/handlers/login.handler';

interface LoginFormShape {
  email: FormControl<string>;
  password: FormControl<string>;
}

@Component({
  selector: 'auth-login-page',
  templateUrl: './login.page.html',
  styleUrls: ['./login.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class LoginPage {
  readonly registerPath: string = `/${AppRoutes.AUTH}/${AuthRoutes.REGISTER}`;
  readonly resetPasswordPath: string = `/${AppRoutes.AUTH}/${AuthRoutes.RESET_PASSWORD}`;
  readonly welcomePath: string = `/${AppRoutes.AUTH}/${AuthRoutes.WELCOME}`;

  readonly loading: WritableSignal<boolean> = signal<boolean>(false);
  readonly errorMessage: WritableSignal<string | null> = signal<string | null>(null);

  readonly form: FormGroup<LoginFormShape> = new FormGroup<LoginFormShape>({
    email: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email]
    }),
    password: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(8)]
    })
  });

  private readonly loginHandler: LoginHandler = inject(LoginHandler);
  private readonly authWithGoogleHandler: AuthWithGoogleHandler = inject(AuthWithGoogleHandler);
  private readonly authWithAppleHandler: AuthWithAppleHandler = inject(AuthWithAppleHandler);
  private readonly platform: PlatformProvider = inject(PLATFORM_TOKEN);
  private readonly env: EnvConfig = inject(ENV_CONFIG);
  private readonly router: Router = inject(Router);
  private readonly translate: TranslateService = inject(TranslateService);

  // Na local/stage zawsze (testy UI); na prod tylko dla natywnego iOS (App Store Guideline 4.8).
  readonly showAppleSignIn: boolean = !this.env.production || this.platform.isiOS;

  emailError(): string | null {
    const ctrl: FormControl<string> = this.form.controls.email;
    if (ctrl.touched && ctrl.invalid) return this.translate.instant('auth.errors.emailFormat');
    return null;
  }

  passwordError(): string | null {
    const ctrl: FormControl<string> = this.form.controls.password;
    if (ctrl.touched && ctrl.invalid) return this.translate.instant('auth.errors.passwordMin');
    return null;
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const { email, password } = this.form.getRawValue();
    this.runAuth((): Observable<void> => this.loginHandler.handle({ email, password }));
  }

  onGoogleClick(): void {
    this.runAuth((): Observable<void> => this.authWithGoogleHandler.handle());
  }

  onAppleClick(): void {
    this.runAuth((): Observable<void> => this.authWithAppleHandler.handle());
  }

  goBack(): void {
    void this.router.navigate([this.welcomePath]);
  }

  goForgot(): void {
    void this.router.navigate([this.resetPasswordPath]);
  }

  private runAuth(action: () => Observable<void>): void {
    this.loading.set(true);
    this.errorMessage.set(null);
    action()
      .pipe(take(1))
      .subscribe({
        complete: (): void => this.loading.set(false),
        error: (err: Error): void => {
          this.loading.set(false);
          this.errorMessage.set(err.message);
        }
      });
  }
}
